/**
 * Client-side Studio Team helpers: the isomorphic block-parsing logic now
 * lives in src/shared/studioTeamBlockParser.ts (shared with server-side
 * executors); this file re-exports it for existing import sites and adds
 * saveStudioTeamToVault, which needs the browser-only fetchWithCsrf.
 */

export {
  extractBlocks,
  parseTitleArtist,
  parseStudioTeamOutput,
  validateBlocks,
  type StudioTeamBlocks,
} from '@/shared/studioTeamBlockParser';

import type { StudioTeamBlocks } from '@/shared/studioTeamBlockParser';

/**
 * Persist a parsed Studio Team song to the DraftVault. Single source of
 * truth for both the manual Save-to-Vault button on StudioTeamPanel and
 * the auto-save fired by Roulette's Spin & Send pipeline.
 *
 * Throws on failure — callers decide whether to retry/surface to the UI.
 */
export async function saveStudioTeamToVault(
  blocks: StudioTeamBlocks,
  rawText: string,
  opts: {
    provider?: string; model?: string; tags?: string; stylePresetName?: string;
    /** What the user asked for -- stored as the session's formDataSnapshot. */
    brief?: Record<string, unknown>;
    /** Shown as the session's genre badge in Session History. */
    genre?: string;
  } = {},
): Promise<string | null> {
  const { fetchWithCsrf } = await import('./fetchWithCsrf');
  // `fullResponse` is not a DraftVault column and is not in the server's
  // field whitelist as a top-level key -- it has to travel inside
  // originalData (a LongText JSON field), the same way the upload-md path
  // already stores it (see server/routes/drafts.routes.ts). Sending it
  // top-level here silently dropped it on every Studio Team save until
  // now (Forgejo #89) -- there was no way to recover the raw pre-extraction
  // response for a broken generation after the fact.
  const originalData = {
    titleArtist: blocks.title && blocks.artist ? `${blocks.title} - ${blocks.artist}` : blocks.title,
    title: blocks.title,
    artist: blocks.artist,
    summary: blocks.summary,
    style: blocks.style,
    excludedStyle: blocks.excludedStyle,
    lyricsWithTags: blocks.lyrics,
    artPrompt: blocks.imagePrompt,
    fullResponse: rawText,
  };
  const payload = {
    title: blocks.title.slice(0, 500),
    artist: blocks.artist.slice(0, 200),
    summary: blocks.summary.slice(0, 2000),
    style: blocks.style.slice(0, 1000),
    excludedStyle: blocks.excludedStyle.slice(0, 1000),
    lyricsWithTags: blocks.lyrics.slice(0, 5000),
    artPrompt: blocks.imagePrompt ? blocks.imagePrompt.slice(0, 20000) : undefined,
    sourceType: 'generated' as const,
    tags: opts.tags ?? 'studio-team-v21',
    llmProvider: opts.provider,
    llmModel: opts.model,
    originalData: JSON.stringify(originalData),
    stylePresetName: opts.stylePresetName,
  };
  const res = await fetchWithCsrf('/api/drafts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || err.message || `Save failed (${res.status})`);
  }
  const draft = await res.json() as { id?: string };
  const draftId = draft.id ? String(draft.id) : null;
  window.dispatchEvent(new CustomEvent('hookhouse:vault-updated', { detail: { draftId } }));
  if (draftId) void logStudioTeamSession(draftId, opts);
  return draftId;
}

/**
 * Record a GenerationSession for a saved Studio Team song, the way RiffWriter
 * does after its auto-save -- without it every Studio Team song was missing
 * from Session History, ratings and analytics (Forgejo #130). Non-fatal: the
 * song is already in the Vault, so a failure here only warns.
 */
async function logStudioTeamSession(
  draftId: string,
  opts: { provider?: string; model?: string; brief?: Record<string, unknown>; genre?: string; stylePresetName?: string },
): Promise<void> {
  const { fetchWithCsrf } = await import('./fetchWithCsrf');
  const { API_ENDPOINTS } = await import('@/config/api');
  const genre = (opts.genre || opts.stylePresetName || '').trim();
  try {
    const res = await fetchWithCsrf(API_ENDPOINTS.sessions.create, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        draftId,
        formDataSnapshot: JSON.stringify({ source: 'studio-team', ...(opts.brief || {}) }).slice(0, 50000),
        genre: genre ? genre.slice(0, 200) : undefined,
        llmProvider: opts.provider,
        llmModel: opts.model,
      }),
    });
    // fetch resolves on HTTP errors -- check, or a rejected save is invisible (AUTO-043).
    if (!res.ok) console.warn(`[studio-team] Session log rejected: HTTP ${res.status}`);
  } catch (err) {
    console.warn('[studio-team] Session log failed (non-fatal):', err);
  }
}
